import { QUERY_FETCH_POLICY } from 'core/hooks/useQueryHelper';
import { AppState } from 'core/redux/rootReducer';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloError, useLazyQuery } from '@apollo/client';
import { getNormalizedExtraServiceOrders } from '../../../utils/useExtraServiceHelper';
import { ExtraServicesOrdersQueryDocument } from './useQueryExtraServicesOrders';
import { IOrderedExtraServiceFromApi } from 'extraService/types/extraServices';

type ApiResponse = { orderedExtraServices: IOrderedExtraServiceFromApi[] };

type UseLazyQueryExtraServicesOrdersResult = {
    loading: boolean;
    error: ApolloError | undefined;
    orders: ReturnType<typeof getNormalizedExtraServiceOrders>;
    called: boolean;
    getOrders: () => Promise<unknown>;
};

export default function useLazyQueryExtraServicesOrders(): UseLazyQueryExtraServicesOrdersResult {
    const dispatch = useDispatch();
    const { i18n } = useTranslation();

    const selectedAgreementId = useSelector(({ agreements }: AppState) => agreements.selectedAgreement?.id);
    const selectedAgreementParentId = useSelector(
        ({ agreements }: AppState) => agreements.selectedAgreement?.parentAgreementId,
    );

    const rentalAgreementId = selectedAgreementParentId || selectedAgreementId;

    const [getOrders, { data, loading, error, called }] = useLazyQuery<ApiResponse>(ExtraServicesOrdersQueryDocument, {
        ...QUERY_FETCH_POLICY,
    });

    const orders = getNormalizedExtraServiceOrders(i18n.language, data?.orderedExtraServices ?? []);

    useEffect(() => {
        if (!called) return;
        dispatch({
            type: 'EXTRA_SERVICE/SET_EXTRA_SERVICES_LOADING',
            payload: loading,
        });
    }, [called, dispatch, loading]);

    useEffect(() => {
        if (!called) return;
        dispatch({
            type: 'EXTRA_SERVICE/SET_ORDERED_EXTRA_SERVICES',
            payload: orders,
        });
    }, [called, dispatch, orders]);

    return {
        loading,
        error,
        orders,
        called,
        getOrders: async () => {
            if (!rentalAgreementId) return;
            await getOrders({
                variables: { rentalAgreementId },
            });
        },
    };
}
